import {Component, inject, OnInit, effect} from '@angular/core';
import {MatDialogRef} from "@angular/material/dialog";
import {MatButton, MatIconButton} from "@angular/material/button";
import {MatDatepicker, MatDatepickerInput, MatDatepickerToggle} from "@angular/material/datepicker";
import {MatFormField, MatHint, MatLabel, MatSuffix, MatError} from "@angular/material/form-field";
import {MatGridList, MatGridTile} from "@angular/material/grid-list";
import {MatIcon} from "@angular/material/icon";
import {MatInput} from "@angular/material/input";
import {ReactiveFormsModule} from "@angular/forms";
import {SharedModule} from "../../../shared/shared.module";
import {BreakpointObserver, Breakpoints} from "@angular/cdk/layout";
import {AdminPanelFilterService} from "../../service/admin-panel-filter.service";
import {AdminPanelFilterModel} from "../../models/admin-panel-filter-model";
import {DateRangeHelper, getDateRangeErrorMessage, shouldShowClearIcon} from "../../../shared/validators";
import moment from 'moment';

@Component({
  selector: 'app-admin-panel-filter-modal',
  imports: [
    MatButton,
    MatDatepicker,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatFormField,
    MatGridList,
    MatGridTile,
    MatHint,
    MatIcon,
    MatIconButton,
    MatInput,
    MatLabel,
    MatSuffix,
    MatError,
    ReactiveFormsModule,
    SharedModule
  ],
  templateUrl: './admin-panel-filter-modal.component.html',
  styles: [`
    .filter__validation-message {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 12px 16px;
      margin-top: 16px;
      background-color: #fff3e0;
      border-left: 4px solid #ff9800;
      border-radius: 4px;
      color: #e65100;
      font-size: 14px;
    }
    
    .filter__validation-message mat-icon {
      font-size: 20px;
      width: 20px;
      height: 20px;
    }
  `]
})
export class AdminPanelFilterModalComponent implements OnInit {
  private readonly dialogRef = inject(MatDialogRef<AdminPanelFilterModalComponent>);

  private readonly adminPanelFilterService = inject(AdminPanelFilterService);

  private readonly breakpointObserver = inject(BreakpointObserver);

  protected readonly form = this.adminPanelFilterService.form;

  protected readonly dateRange = new DateRangeHelper(
    this.form.controls.DateFrom,
    this.form.controls.DateTo,
    { fromMin: moment().subtract(1, 'month').toDate() }
  );

  protected isSmall: boolean = false;

  protected get showDateFromClearIcon(): boolean {
    return shouldShowClearIcon(this.form.controls.DateFrom);
  }

  protected get showDateToClearIcon(): boolean {
    return shouldShowClearIcon(this.form.controls.DateTo);
  }

  protected get isFormValid(): boolean {
    return this.form.valid;
  }

  protected get dateFromError(): string | null {
    return getDateRangeErrorMessage(this.form.controls.DateFrom, 'Date From');
  }

  protected get dateToError(): string | null {
    return getDateRangeErrorMessage(this.form.controls.DateTo, 'Date To');
  }

  constructor() {
    this.breakpointObserver.observe([
      Breakpoints.Medium,
      Breakpoints.Small,
      Breakpoints.XSmall
    ]).subscribe(result => {
      this.isSmall = result.matches;
    });

    effect(() => {
      this.dateRange.fromMin();
      this.dateRange.fromMax();
      this.dateRange.toMin();
      this.dateRange.toMax();
    });
  }

  public ngOnInit() {
    this.patchForm();
  }

  private patchForm() {
    const form = this.adminPanelFilterService.filtersValue();
    this.form.patchValue({...form});
  }

  protected resetFilter(control: keyof Omit<AdminPanelFilterModel, 'ReloadGrid'>) {
    this.form.controls[control].reset();
  }

  protected close() {
    this.dialogRef.close();
  }

  protected apply() {
    const form = this.form.getRawValue();
    this.adminPanelFilterService.setFilters({...form, ReloadGrid: true});
    this.close();
  }

}
